import ApplicationModel, { attr } from '../ApplicationModel';
import EstimateTypeFilter, {
  type EstimateTypeFilterValue,
} from '../filters/EstimateTypeFilter';
import type ItemFilter from '../filters/ItemFilter';
import RecordFilter from '../filters/RecordFilter';
import { model } from '../modelBuilder';
import type Iteration from './Iteration';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import { type BookmarksRetrospectiveReportAttributes } from './generated/BookmarksRetrospectiveReportAttributes';
import { type RecordId } from 'javascripts/shared-types';
import { withResolvers } from 'javascripts/util/withResolvers';
import { type UnitType } from '../lib/Estimate';
import { PARSE_ITERATION_RECORD_EVENT_TYPES } from '../lib/RecordEvents/parseRecordData';
import BookmarksIterationBurndown from './BookmarksIterationBurndown';
import BookmarksVelocityChart from './BookmarksVelocityChart';

interface Local {
  availableIterations?: Iteration[];
  iteration?: Iteration;
  iterationPromise: PromiseWithResolvers<Iteration>;
  filterObjects: Record<string, ItemFilter<any>>;
  iterationBurndown: BookmarksIterationBurndown;
  velocityChart: BookmarksVelocityChart;
}

interface BookmarksRetrospectiveReport
  extends BookmarksRetrospectiveReportAttributes,
    HasBookmarkFilters<{
      iterations?: RecordFilter;
      estimation_type?: EstimateTypeFilter;
      releases?: RecordFilter;
    }> {
  local: Local;
}

@model
@HasBookmarkFilters
class BookmarksRetrospectiveReport extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksRetrospectiveReport' =
    'BookmarksRetrospectiveReport';
  get typename(): 'BookmarksRetrospectiveReport' {
    return 'BookmarksRetrospectiveReport';
  }

  static internalTypename: 'Bookmarks::RetrospectiveReport' =
    'Bookmarks::RetrospectiveReport';
  get internalTypename(): 'Bookmarks::RetrospectiveReport' {
    return 'Bookmarks::RetrospectiveReport';
  }

  static bookmarkName = 'Bookmarks::RetrospectiveReport';

  static inputType = 'RetrospectiveReportAttributes!';

  static fields = {
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
  };
  // End auto-generated

  initialize() {
    this.local = {
      filterObjects: {},
      iterationBurndown: new BookmarksIterationBurndown({
        projectId: this.project.id,
      }),
      velocityChart: new BookmarksVelocityChart({ projectId: this.project.id }),
      iterationPromise: withResolvers<Iteration>(),
    };
    this.filters = this.filters || {};
  }

  get iterationBurndown() {
    return this.local.iterationBurndown;
  }

  get velocityChart() {
    return this.local.velocityChart;
  }

  async createFilters() {
    const iterations = await this.availableIterations();

    let iteration: Iteration | undefined;
    if (this.filters.iterations) {
      const iterationId = this.filters.iterations[0];
      iteration = iterations?.find(i => i.id === iterationId);
    }

    if (!iteration && iterations && iterations.length > 0) {
      // Default to most recently completed sprint
      iteration = iterations.find(i => i.completed) || iterations[0];
    }

    const defaultEstimationType = 'originalEstimate';
    const estimationUnits =
      iteration?.originalEstimate?.units === 'POINTS' ? 'points' : 'time';
    const estimationTypes: [string, EstimateTypeFilterValue][] = [
      ['Work completed (records)', 'count'],
      [`Work completed (${estimationUnits})`, 'originalEstimate'],
      [`Effort logged (${estimationUnits})`, 'remainingEstimate'],
    ];

    const estimateTypeFilter = new EstimateTypeFilter(
      this.filters.estimation_type?.length > 0
        ? this.filters.estimation_type
        : [defaultEstimationType],
      estimationTypes
    );

    const nameMap: (item: {
      name: string;
      id: RecordId;
    }) => [string, RecordId] = item => [item.name, item.id];

    const iterationFilter = new RecordFilter(
      this.filters.iterations?.length > 0
        ? this.filters.iterations
        : [iteration?.id],
      iterations?.map(nameMap)
    );

    return {
      iterations: iterationFilter,
      estimation_type: estimateTypeFilter,
    };
  }

  canFilter(_scope) {
    return true;
  }

  iteration() {
    return this.local.iterationPromise?.promise.then(
      () => this.local.iteration
    );
  }

  setIteration(iteration: Iteration) {
    this.local.iteration = iteration;
    this.local.iterationPromise?.resolve(iteration);
  }

  user() {
    return undefined;
  }

  async availableIterations() {
    if (this.local.availableIterations) return this.local.availableIterations;

    const iterations = await this.iterationBurndown.availableIterations();
    this.local.availableIterations = iterations;

    return iterations;
  }

  async availableUsers() {
    return this.velocityChart.availableUsers();
  }

  units(): UnitType {
    const units = this.getFilter('estimation_type')?.single();
    if (units === 'count') {
      return 'COUNT';
    } else {
      return this.local.iteration?.originalEstimate?.units || 'POINTS';
    }
  }

  async eventsRaw(iteration: Iteration) {
    return iteration.eventsRaw({
      eventType: PARSE_ITERATION_RECORD_EVENT_TYPES,
    });
  }
}

export default BookmarksRetrospectiveReport;
